import { useEffect, useRef } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  ogImageWidth?: string;
  ogImageHeight?: string;
  schemaData?: Record<string, unknown>[];
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  ogImageWidth = '1200',
  ogImageHeight = '630',
  schemaData,
}: SEOProps) {
  const schemaRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    const fullTitle = `${title} | 羞羞视频精选合集`;
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl || window.location.href, true);
    setMeta('og:site_name', '羞羞视频精选合集', true);
    setMeta('og:locale', 'zh_CN', true);
    if (ogImage) {
      setMeta('og:image', ogImage, true);
      setMeta('og:image:width', ogImageWidth, true);
      setMeta('og:image:height', ogImageHeight, true);
    }
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    if (ogImage) {
      setMeta('twitter:image', ogImage);
    }

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }

    // Schema.org JSON-LD
    if (schemaData && schemaData.length > 0) {
      if (schemaRef.current) {
        schemaRef.current.remove();
      }
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(
        schemaData.length === 1 ? schemaData[0] : schemaData,
        null,
        0
      );
      document.head.appendChild(script);
      schemaRef.current = script;
    }

    return () => {
      if (schemaRef.current) {
        schemaRef.current.remove();
        schemaRef.current = null;
      }
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, ogImageWidth, ogImageHeight, schemaData]);

  return null;
}